import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertBar } from '@/components/AlertBar';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'motion/react';
import { Timer1 } from 'iconsax-reactjs';
import { MARKER_ZONES, formatMinute } from '@pipyar/core';
import { api } from '@/lib/api';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { useAlertSound } from '@/lib/useAlertSound';
import { formatBrokerTime } from '@/lib/time';

/** How long before a point the alert appears. */
const LEAD_SECONDS = 15 * 60;

interface NextPoint {
  id: number;
  name: string;
  color: string;
  minute: number;
  timezone: string | null;
  nextTime: number;
}

interface NextPointsResponse {
  symbol: string;
  serverTime: number;
  points: NextPoint[];
}

/**
 * Bottom-left countdown that appears fifteen minutes before a time point.
 *
 * Lives in the app shell rather than on the Time Windows page: a point is worth
 * knowing about whichever screen you happen to be on, and the whole value of an
 * advance warning is that it reaches you when you are not already looking at the
 * thing it concerns.
 */
export function PointAlert() {
  const { symbol, instrument } = useSymbol();
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['next-points', symbol],
    queryFn: () => api.get<NextPointsResponse>(`/api/market/points/next?symbol=${encodeURIComponent(symbol!)}`),
    enabled: Boolean(symbol),
    // Shares the 'next-points' key with the dashboard tile, so it shares its
    // polling too: divergent options on one key make the effective interval
    // depend on which observer mounted first.
    refetchInterval: 5_000,
    staleTime: 4_000,
  });

  const [now, setNow] = useState(() => Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 1000);
    return () => clearInterval(id);
  }, []);

  // Trust the server's clock over this machine's, re-derived on every response.
  const serverTime = query.data?.serverTime;
  const [skew, setSkew] = useState(0);
  useEffect(() => {
    if (serverTime !== undefined) setSkew(serverTime - Date.now() / 1000);
  }, [serverTime]);

  /*
   * Keyed by occurrence, not by point.
   *
   * Dismissing tomorrow's London Open along with today's would silence the
   * alert for a marker you only meant to clear off the screen once.
   */
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const clockNow = now + skew;
  const points = useMemo(() => query.data?.points ?? [], [query.data]);

  const active = useMemo(() => {
    for (const p of points) {
      const until = p.nextTime - clockNow;
      if (until <= 0 || until > LEAD_SECONDS) continue;
      if (dismissed.has(`${p.id}:${p.nextTime}`)) continue;
      return { point: p, until };
    }
    return null;
  }, [points, clockNow, dismissed]);

  /*
   * Announce each occurrence once.
   *
   * `active` is derived, not an event: it recomputes every second as the
   * countdown ticks, so playing on its truthiness would sound once a second for
   * the whole lead time. Keyed by occurrence for the same reason dismissal is.
   */
  const { play } = useAlertSound('point');
  const playRef = useRef(play);
  playRef.current = play;
  const announced = useRef<string | null>(null);
  const activeKey = active ? `${active.point.id}:${active.point.nextTime}` : null;

  useEffect(() => {
    if (activeKey === null || announced.current === activeKey) return;
    announced.current = activeKey;
    playRef.current();
  }, [activeKey]);

  const zone = active?.point.timezone
    ? (MARKER_ZONES.find((z) => z.id === active.point.timezone)?.label ?? active.point.timezone)
    : t('points.broker');

  return (
    <AnimatePresence>
      {active && (
        <AlertBar
          key={`${active.point.id}:${active.point.nextTime}`}
          accent={active.point.color}
          icon={<Timer1 size={16} variant="Bold" />}
          kind={t('points.alertKind')}
          live={active.until <= 60}
          title={active.point.name}
          subtitle={
            <>
              {`${formatMinute(active.point.minute)} ${zone}`}
              {' · '}
              {/* When this occurrence lands, on the broker clock the charts use. */}
              <span className="tabular" title={t('common.brokerTime')}>
                {formatBrokerTime(
                  active.point.nextTime,
                  instrument?.storedGmtOffsetSec ?? 0,
                  t('common.today'),
                )}
              </span>
            </>
          }
          valueCaption={t('points.untilFires')}
          value={formatMs(active.until)}
          // Drains over the lead time, driven by the same clock as the digits.
          progress={{ value: active.until / LEAD_SECONDS, color: active.point.color }}
          onClose={() =>
            setDismissed((prev) => new Set(prev).add(`${active.point.id}:${active.point.nextTime}`))
          }
          closeLabel={t('points.dismiss', { name: active.point.name })}
        />
      )}
    </AnimatePresence>
  );
}

/** mm:ss — inside the lead time the seconds are the point. */
function formatMs(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
